import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getOrderColumns } from './api';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ArrowUpDown, Settings } from 'lucide-react';

import { GlobalTable, HeaderLine, Td, Tr, FooterTr } from '../../../../components/styles/table-style';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import { Th } from './style';
import {
  CenteredCell,
  ErrorMessage,
  MessageContainer,
  TableContainer,
  StatusBadge,
  ConfigMenuWrapper,
  ConfigMenuButton,
  ColumnMenu,
  ColumnMenuList,
  ColumnMenuItem,
  ThContent,
  HiddenCheckbox,
  StyledCheckbox
} from './style';
import { formatCurrency, formatDate, getTotalWeight, getStatusText } from './utils';
import { fetchPaginatedOrders, ITEMS_PER_PAGE } from './api';
import { SteelOrder } from './types';
import { Filter } from '../../../../components/SubHeaderFilter/types';
import { Button } from '../AddOrder/style';
import ActionsDropdown from './ActionsDropdown';

interface OutletContextType {
  refresh: boolean;
  appliedFilters: Filter[];
  search: string;
}

const SteelOrdersTable: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { refresh, appliedFilters, search } = useOutletContext<OutletContextType>();
  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'id', desc: true }
  ]);
  const [isCustomOrder, setIsCustomOrder] = useState(false);
  const [customOrderedData, setCustomOrderedData] = useState<SteelOrder[] | null>(null);
  const [duplicatedOrderIds, setDuplicatedOrderIds] = useState<number[]>([]);
  const prevFlatDataRef = useRef<SteelOrder[]>([]);
  const columnMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setShowColumnMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  

  useEffect(() => {
    setIsCustomOrder(false);
    setCustomOrderedData(null);
    queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
  }, [refresh, queryClient]);

  const {
    data: queryData,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isError,
  } = useInfiniteQuery<any, Error, any>({
    queryKey: ['steelOrders', appliedFilters, search],
    queryFn: async ({ pageParam = 1 }: { pageParam: unknown }) => {
      return fetchPaginatedOrders({
        pageParam: pageParam as number,
        filter: appliedFilters,
        search: search,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.data) return undefined;
      const totalFetched = lastPage.page * ITEMS_PER_PAGE;
      if (totalFetched < lastPage.total) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    select: (queryData) => {
      return queryData;
    },
  });



  const [allColumns, setAllColumns] = useState<{ key: string; label: string }[]>([]);
  const [extraColumns, setExtraColumns] = useState<string[]>([]);

  useEffect(() => {
    getOrderColumns().then(setAllColumns).catch(() => setAllColumns([]));
  }, []);

  const defaultColumns: ColumnDef<SteelOrder>[] = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: (info) => {
        const id = info.getValue() as number;
        const isDuplicated = duplicatedOrderIds.includes(id);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{id}</span>
            {isDuplicated && (
              <span style={{
                backgroundColor: '#ff6b6b',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}>
                DUPLICADO
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'client',
      header: 'Cliente',
    },
    {
      accessorKey: 'orderDate',
      header: 'Data do Pedido',
      cell: (info) => formatDate(info.getValue() as string),
    },
    {
      accessorKey: 'deliveryDate',
      header: 'Prazo de Entrega',
      cell: (info) => formatDate(info.getValue() as string),
    },
    {
      accessorKey: 'items',
      header: 'Peso Total',
      cell: (info) => getTotalWeight(info.getValue() as any[]),
    },
    {
      accessorKey: 'total',
      header: 'Valor Total',
      cell: (info) => formatCurrency(info.getValue() as number),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => {
        const status = info.getValue() as boolean | null | undefined;
        return (
          <StatusBadge status={status}>
            {getStatusText(status)}
          </StatusBadge>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      enableSorting: false,
      cell: ({ row }) => <ActionsDropdown order={row.original} />,
    },
  ], [duplicatedOrderIds]);

  const extraColumnDefs: ColumnDef<SteelOrder>[] = useMemo(() =>
    allColumns
      .filter(col =>
        !defaultColumns.some(def => {
          const defId = (def as any).id ?? (def as any).accessorKey;
          return defId === col.key;
        }) &&
        extraColumns.includes(col.key)
      )
      .map(col => ({
        accessorKey: col.key,
        header: col.label,
        cell: (info: any) => String(info.getValue?.() ?? ''),
      })),
    [allColumns, defaultColumns, extraColumns]
  );

  const columns = useMemo<ColumnDef<SteelOrder>[]>(
    () => [...defaultColumns, ...extraColumnDefs],
    [defaultColumns, extraColumnDefs]
  );

  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const flatData = useMemo(() => {
    if (Array.isArray(queryData)) {
      return queryData;
    }

    if (queryData && queryData.pages && Array.isArray(queryData.pages)) {
      return queryData.pages.flatMap((page: any) => page.data ?? []);
    }

    return [];
  }, [queryData]);

  useEffect(() => {
    const lastDuplicatedFrom = localStorage.getItem('lastDuplicatedFrom');

    if (lastDuplicatedFrom && flatData.length > 0) {
      const originalOrderId = parseInt(lastDuplicatedFrom, 10);

      const originalOrder = flatData.find((o: SteelOrder) => o.id === originalOrderId);

      if (!originalOrder) {
        localStorage.removeItem('lastDuplicatedFrom');
        prevFlatDataRef.current = flatData;
        return;
      }

      const baseData = customOrderedData || prevFlatDataRef.current;
      const baseDataIds = new Set(baseData.map((o: SteelOrder) => o.id));

      const newDuplicatedOrder = flatData.find((order: SteelOrder) => {
        const notInBase = !baseDataIds.has(order.id);
        const notMarked = !duplicatedOrderIds.includes(order.id);
        const sameClient = order.client === originalOrder.client;
        const hasDate = order.orderDate && originalOrder.orderDate;
        const timeDiff = hasDate ? Math.abs(new Date(order.orderDate!).getTime() - new Date(originalOrder.orderDate!).getTime()) : Infinity;
        const closeTime = timeDiff < 60000;

        return notInBase && notMarked && sameClient && hasDate && closeTime;
      });

      if (newDuplicatedOrder) {
        let reorderedData: SteelOrder[];

        if (customOrderedData) {
          reorderedData = [...customOrderedData];

          const alreadyExists = reorderedData.some(o => o.id === newDuplicatedOrder.id);
          if (!alreadyExists) {
            reorderedData.push(newDuplicatedOrder);
          }
        } else {
          reorderedData = [...flatData];
        }

        const existingIndex = reorderedData.findIndex((order: SteelOrder) => order.id === newDuplicatedOrder.id);
        if (existingIndex !== -1) {
          reorderedData.splice(existingIndex, 1);
        }

        const originalIndex = reorderedData.findIndex((order: SteelOrder) => order.id === originalOrderId);

        if (originalIndex !== -1) {
          reorderedData.splice(originalIndex + 1, 0, newDuplicatedOrder);

          setCustomOrderedData(reorderedData);
          setIsCustomOrder(true);
          setDuplicatedOrderIds(prev => [...prev, newDuplicatedOrder.id]);

          localStorage.removeItem('lastDuplicatedFrom');
        }

        prevFlatDataRef.current = flatData;
        return;
      }

      if (flatData.length > prevFlatDataRef.current.length) {
        localStorage.removeItem('lastDuplicatedFrom');
      }

      prevFlatDataRef.current = flatData;
      return;
    }

    prevFlatDataRef.current = flatData;

  }, [flatData, customOrderedData, duplicatedOrderIds, isCustomOrder]);

  useEffect(() => {
    if (isCustomOrder && customOrderedData) {
      if (customOrderedData.length > flatData.length) {
        const flatDataIds = new Set(flatData.map((item: SteelOrder) => item.id));
        const filteredCustomData = customOrderedData.filter((item: SteelOrder) => flatDataIds.has(item.id));

        if (filteredCustomData.length < customOrderedData.length) {
          setCustomOrderedData(filteredCustomData);
        }
      }

      if (flatData.length > customOrderedData.length) {
        const customDataIds = new Set(customOrderedData.map((item: SteelOrder) => item.id));
        const newItems = flatData.filter((item: SteelOrder) => !customDataIds.has(item.id));

        if (newItems.length > 0) {
          const updatedData = [...customOrderedData, ...newItems];
          setCustomOrderedData(updatedData);
        }
      }
    }
  }, [flatData, isCustomOrder, customOrderedData]);

  const table = useReactTable({
    data: customOrderedData ?? flatData ?? [],
    columns,
    state: {
      sorting: isCustomOrder ? [] : sorting,
      columnVisibility,
    },
    onSortingChange: isCustomOrder ? undefined : setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: isCustomOrder,
    meta: {},
  });

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  if (isPending) {
    return <MessageContainer>Carregando pedidos...</MessageContainer>;
  }

  if (isError) {
    return <ErrorMessage>Não foi possível carregar os pedidos. Erro: {error?.message}</ErrorMessage>;
  }

  return (
    <TableContainer>
      <GlobalTable>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <HeaderLine key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <Th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  $sortable={header.column.getCanSort()}
                >
                  <ThContent>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      {
                        asc: <ArrowUp size={14} />,
                        desc: <ArrowDown size={14} />,
                      }[header.column.getIsSorted() as string] ?? <ArrowUpDown size={14} style={{ opacity: 0.4 }} />
                    )}
                  </ThContent>
                </Th>
              ))}
              <th style={{ width: '32px', background: '#f5f5f5', border: 0, padding: '0 4px', verticalAlign: 'middle' }}>
                <ConfigMenuWrapper ref={columnMenuRef}>
                  <ConfigMenuButton
                    onClick={() => setShowColumnMenu((v) => !v)}
                    title="Mostrar/ocultar colunas"
                    style={{ color: '#bdbdbd' }}
                  >
                    <Settings size={20} />
                  </ConfigMenuButton>
                  {showColumnMenu && (
                    <ColumnMenu>
                      <ColumnMenuList>
                        {allColumns.filter(col => col.key !== 'actions').map(col => {
                          const isDefault = defaultColumns.some(def => {
                            const defId = (def as any).id ?? (def as any).accessorKey;
                            return defId === col.key;
                          });
                          const isVisible = isDefault
                            ? table.getColumn(col.key)?.getIsVisible() ?? false
                            : extraColumns.includes(col.key);
                          return (
                            <ColumnMenuItem key={col.key}>
                              <HiddenCheckbox
                                checked={isVisible}
                                onChange={e => {
                                  if (isDefault) {
                                    table.setColumnVisibility({
                                      ...table.getState().columnVisibility,
                                      [col.key]: e.target.checked
                                    });
                                  } else {
                                    setExtraColumns(cols =>
                                      e.target.checked
                                        ? [...cols, col.key]
                                        : cols.filter(k => k !== col.key)
                                    );
                                  }
                                }}
                                id={`col-visibility-${col.key}`}
                              />
                              <StyledCheckbox
                                checked={isVisible}
                                onClick={() => {
                                  const newValue = !isVisible;
                                  if (isDefault) {
                                    table.setColumnVisibility({
                                      ...table.getState().columnVisibility,
                                      [col.key]: newValue
                                    });
                                  } else {
                                    setExtraColumns(cols =>
                                      newValue
                                        ? [...cols, col.key]
                                        : cols.filter(k => k !== col.key)
                                    );
                                  }
                                }}
                              />
                              <label htmlFor={`col-visibility-${col.key}`}>
                                {col.label}
                              </label>
                            </ColumnMenuItem>
                          );
                        })}
                      </ColumnMenuList>
                    </ColumnMenu>
                  )}
                </ConfigMenuWrapper>
              </th>
            </HeaderLine>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map(row => (
              <Tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
                ))}
                <td style={{ border: 0 }} />
              </Tr>
            ))
          ) : (
            <Tr>
              <CenteredCell colSpan={columns.length + 1}>Nenhum pedido encontrado.</CenteredCell>
            </Tr>
          )}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td colSpan={columns.length} style={{ textAlign: 'center', background: 'transparent' }}>
              <LoadMoreButton
                onLoadMore={handleLoadMore}
                isLoading={isFetchingNextPage}
                hasMore={hasNextPage}
                currentCount={flatData.length}
                totalCount={queryData && Array.isArray(queryData.pages) && queryData.pages.length > 0 ? queryData.pages[0].total : undefined}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </GlobalTable>


    </TableContainer>
  );
};

export default SteelOrdersTable;